/**
 * BFS Search Overlay
 * Instant search functionality with AJAX results
 */

(function() {
  'use strict';

  const DEBOUNCE_DELAY = 300;
  const MIN_SEARCH_LENGTH = 1;

  let searchOverlay = null;
  let searchInput = null;
  let searchForm = null;
  let searchResults = null;
  let closeButton = null;
  let searchTriggers = [];

  let clearButton = null;

  let isOpen = false;
  let currentQuery = '';
  let searchTimeout = null;
  let hasResults = false;
  let abortController = null;
  let defaultStateHTML = null;
  let scrollPosition = 0;

  function init() {
    searchOverlay = document.getElementById('bfs-search-overlay');
    if (!searchOverlay) return;

    searchInput = document.getElementById('bfs-search-overlay-input');
    searchForm = searchOverlay.querySelector('.bfs-search-overlay__form');
    searchResults = document.getElementById('bfs-search-results');
    closeButton = searchOverlay.querySelector('[data-action="close-search-overlay"]');
    clearButton = searchOverlay.querySelector('.bfs-search-overlay__clear');

    searchTriggers = document.querySelectorAll(
      '.bfs-navigation__search-drawer-trigger, ' +
      '.bfs-search-form input[type="search"], ' +
      '.bfs-search-form input[type="text"], ' +
      '[data-action="open-search"], ' +
      'input.searchinput, ' +
      '#searchForm input[type="text"], ' +
      '.bfs-mobile-navigation__search input, ' +
      '.bfs-search-drawer__form input'
    );

    if (!searchInput || !searchForm || !searchResults) return;

    cacheDefaultState();
    bindEvents();
    updateAriaAttributes();

    // Browsers restore a typed <input>'s value on back/forward navigation
    // independently of whatever server-rendered HTML comes back with it —
    // clicking a collection link from search results navigates to a new
    // page entirely (handleCollectionLinkClick below), and going back to
    // it can land here with searchInput.value already restored to the old
    // query while searchResults still only has the fresh default state
    // this page rendered, with nothing that ran the actual search. Left
    // alone, that's an inconsistent state: the field shows the old term
    // but the panel doesn't match it. Re-running the search for whatever
    // value came back keeps the two in sync — if the overlay is opened
    // later, open() below sees currentQuery already set and skips its own
    // showDefaultState() call, so the matching results are just already
    // there.
    //
    // This check alone turned out not to be enough on its own: this
    // restoration can land AFTER this script's own init() has already run
    // (it's a separate, later step of the browser's own page-restore
    // process, not something that fires an 'input'/'change' event
    // handleSearchInput would otherwise catch) — so searchInput.value was
    // still genuinely empty at this exact point, and nothing ever
    // re-checked it once the browser filled it in a moment later. Kept
    // here as a same-tick check for the (likely rare) case the browser
    // restores it before this runs, but see the pageshow listener below
    // for the actual fix — that's what catches the delayed case.
    if (searchInput.value && searchInput.value.trim().length >= MIN_SEARCH_LENGTH) {
      currentQuery = searchInput.value.trim();
      performSearch(currentQuery);
    }
  }

  // pageshow fires once the page is actually fully ready to interact with
  // — including after the browser's own delayed form-value restoration on
  // back/forward navigation, which can land after init() above already
  // ran and doesn't fire any event that code was listening for. Re-checks
  // the same condition as the end of init(), guarded against re-running
  // for a query init() already caught (or that's already showing) so this
  // doesn't fire a redundant second search on an ordinary page load where
  // nothing needed restoring in the first place.
  window.addEventListener('pageshow', function() {
    if (!searchInput) return;
    var restoredQuery = (searchInput.value || '').trim();
    if (restoredQuery && restoredQuery.length >= MIN_SEARCH_LENGTH && restoredQuery !== currentQuery) {
      currentQuery = restoredQuery;
      performSearch(restoredQuery);
    }
  });

  /**
   * Cache the initial default state HTML so we never need to re-fetch it.
   */
  function cacheDefaultState() {
    if (searchResults && searchResults.innerHTML.trim().length > 0) {
      defaultStateHTML = searchResults.innerHTML;
    }
  }

  function bindEvents() {
    searchTriggers.forEach(function(trigger) {
      if (trigger.tagName === 'INPUT') {
        trigger.addEventListener('focus', handleSearchTrigger);
        trigger.addEventListener('click', handleSearchTrigger);
        var form = trigger.closest('form');
        if (form && (
          form.id === 'searchForm' ||
          form.classList.contains('Form') ||
          form.classList.contains('bfs-search-form') ||
          (form.id && form.id.includes('bfsSearchForm'))
        )) {
          form.addEventListener('submit', function(e) {
            e.preventDefault();
            handleSearchTrigger(e);
            return false;
          });
        }
      } else {
        trigger.addEventListener('click', handleSearchTrigger);
      }
    });

    var allSearchForms = document.querySelectorAll('form[action*="/search"], .bfs-search-form, #searchForm');
    allSearchForms.forEach(function(form) {
      if (!form.id || !form.id.includes('bfs-search-overlay')) {
        form.addEventListener('submit', function(e) {
          var overlayForm = searchOverlay ? searchOverlay.querySelector('form') : null;
          if (form !== overlayForm) {
            e.preventDefault();
            open();
            return false;
          }
        });
      }
    });

    searchInput.addEventListener('input', handleSearchInput);
    searchInput.addEventListener('keydown', handleKeyDown);
    searchForm.addEventListener('submit', handleFormSubmit);

    if (closeButton) {
      closeButton.addEventListener('click', close);
    }

    if (clearButton) {
      clearButton.addEventListener('click', handleClearClick);
    }

    searchOverlay.addEventListener('click', function(e) {
      if (e.target === searchOverlay) {
        close();
      }
    });

    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && isOpen) {
        close();
      }
    });

    var oldSearchModal = document.getElementById('Search');
    if (oldSearchModal) {
      oldSearchModal.style.display = 'none';
      oldSearchModal.style.visibility = 'hidden';
    }

    if (window.boostPFS) {
      var searchInputs = document.querySelectorAll('input[name="q"]');
      searchInputs.forEach(function(input) {
        if (input.id !== 'bfs-search-overlay-input') {
          var newInput = input.cloneNode(true);
          input.parentNode.replaceChild(newInput, input);
        }
      });
    }

    var boostSuggestions = document.querySelectorAll('.boost-pfs-search-suggestion, .bc-sf-search-suggestion');
    boostSuggestions.forEach(function(el) {
      el.style.display = 'none';
    });
  }

  function handleSearchTrigger(e) {
    e.preventDefault();
    open();

    // The nav's own search-bar trigger (layout/theme.liquid) is styled to
    // look like a real text field and even carries cursor:text on hover —
    // clicking it should continue that "clicked into a field to type"
    // behavior by landing in the real input already focused, unlike the
    // plain magnifying-glass icon trigger, which deliberately does NOT
    // auto-focus on open (removed earlier specifically so the rotating
    // placeholder animation stays visible instead of being immediately
    // paused-by-focus before anyone sees it — see open()'s own history).
    // Scoped to just this one trigger rather than restoring focus-on-open
    // generally, so that earlier fix isn't undone for the icon.
    //
    // Retries alone (a first attempt here) didn't fix this — confirmed
    // live via document.activeElement at the moment it silently failed:
    // Boost PFS had already replaced the real input with its own clone,
    // carrying a DIFFERENT id ("boost-pfs-search-box-4", not
    // "bfs-search-overlay-input") — already known behavior elsewhere in
    // this file's own comments ("Boost PFS clones and replaces this input
    // out from under any cached reference"), just not yet accounted for
    // here. The module-level `searchInput` variable is cached once in
    // init() and never re-queried, so calling .focus() on it was focusing
    // a stale, likely-detached node while the real live input sat under a
    // different id entirely. Re-querying fresh by class (which survives
    // Boost's swap; only the id changes) rather than trusting the cached
    // reference, on every attempt including the retries.
    if (e.currentTarget && e.currentTarget.classList.contains('bfs-nav-search-trigger')) {
      function focusLiveInput() {
        var liveInput = document.querySelector('.bfs-search-overlay__input');
        if (liveInput) liveInput.focus();
      }
      focusLiveInput();
      if (window.requestAnimationFrame) {
        window.requestAnimationFrame(focusLiveInput);
      }
      setTimeout(focusLiveInput, 50);
      setTimeout(focusLiveInput, 250);
    }
  }

  /**
   * Clear (X) button — sits in the same slot as the magnifying glass and
   * is shown in its place for as long as the overlay is open (pure CSS,
   * see .bfs-search-overlay.is-open in the section's own <style>). Mirrors
   * the mobile drawer's own clear button: empty the field, cancel whatever
   * search was in flight, drop back to the default state, and refocus so
   * typing again is immediate.
   */
  function handleClearClick(e) {
    e.preventDefault();
    if (!searchInput) return;
    searchInput.value = '';
    searchInput.removeAttribute('data-current-query');
    currentQuery = '';
    if (searchTimeout) {
      clearTimeout(searchTimeout);
      searchTimeout = null;
    }
    cancelInflightRequest();
    showDefaultState();
    searchInput.focus();
  }

  function handleSearchInput(e) {
    var query = e.target.value.trim();

    if (searchTimeout) {
      clearTimeout(searchTimeout);
      searchTimeout = null;
    }

    if (query.length === 0) {
      cancelInflightRequest();
      // Same gap as the mobile drawer's copy of this (snippets/bfs-search-
      // form.liquid) — the abort this triggers hits the 'AbortError' early
      // return above, which deliberately skips hideLoadingIndicator() (a
      // real search taking over handles that itself), so backspacing all
      // the way to empty was the one path that left it stuck.
      hideLoadingIndicator();
      showDefaultState();
      currentQuery = '';
      return;
    }

    currentQuery = query;
    if (searchInput) {
      searchInput.setAttribute('data-current-query', query);
    }

    showLoadingIndicator();

    searchTimeout = setTimeout(function() {
      performSearch(query);
    }, DEBOUNCE_DELAY);
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && currentQuery.length >= MIN_SEARCH_LENGTH) {
      if (hasResults && currentQuery === searchInput.value.trim()) {
        e.preventDefault();
        return false;
      }
      return;
    }
    if (e.key === 'Escape') {
      close();
    }
  }

  function handleFormSubmit(e) {
    var query = searchInput.value.trim();
    if (query.length < MIN_SEARCH_LENGTH) {
      e.preventDefault();
      return false;
    }
    if (currentQuery === query && hasResults) {
      e.preventDefault();
      return false;
    }
    e.preventDefault();
    performSearch(query);
    return false;
  }

  function cancelInflightRequest() {
    if (abortController) {
      abortController.abort();
      abortController = null;
    }
  }

  function performSearch(query) {
    if (query.length < MIN_SEARCH_LENGTH) {
      showDefaultState();
      return;
    }

    cancelInflightRequest();
    abortController = new AbortController();

    showLoadingIndicator();

    var searchUrl = '/search?view=ajax&q=' + encodeURIComponent(query) + '&type=product';
    var signal = abortController.signal;

    fetch(searchUrl, {
      method: 'GET',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      signal: signal
    })
    .then(function(response) {
      if (!response.ok) throw new Error('Search request failed');
      return response.text();
    })
    .then(function(html) {
      if (signal.aborted) return;

      if (searchResults) {
        replaceContent(html);
        searchResults.setAttribute('data-current-query', query);
        highlightSearchTerm(searchResults, query);
        saveRecentSearch(query);

        var productsGrid = searchResults.querySelector('.bfs-search-results__products-grid');
        var noResultsMessage = searchResults.querySelector('.bfs-search-results__no-results');

        if (productsGrid) {
          var productCards = productsGrid.querySelectorAll('.bfs-product-card, .Grid__Cell');
          hasResults = productCards.length > 0;
        } else {
          hasResults = false;
        }
      }

      hideLoadingIndicator();
      initSwipers();
      initSwatchesDebounced();
    })
    .catch(function(error) {
      if (error.name === 'AbortError') return;
      hideLoadingIndicator();
      showErrorState();
    });
  }

  /**
   * Bolds only the exact substring the customer typed within each Top
   * Collections name (e.g. searching "dress" against "Dresses" bolds just
   * "Dress"). Mirrors highlightSearchTerm in snippets/bfs-search-form.liquid
   * — this overlay is a separate, older search implementation (used above
   * the 1280px breakpoint, where the header's search icon opens this
   * standalone overlay instead of the mobile drawer's inline search) that
   * never had its own highlighting, which is why bold only ever showed up
   * on mobile/tablet. Kept as its own copy rather than a shared function
   * since the two files don't share a JS scope.
   */
  // Builds one regex-alternation pattern for a single word — see
  // bfs-search-form.liquid's own copy of this for the full explanation.
  function highlightWordPattern(word) {
    var escaped = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    if (/s$/i.test(word) && word.length > 3) {
      var singularEscaped = word.slice(0, -1).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      return '(?:' + escaped + '|' + singularEscaped + ')';
    }
    return escaped;
  }

  function highlightSearchTerm(container, term) {
    if (!term) return;
    var trimmedTerm = term.trim();
    if (!trimmedTerm) return;
    var words = trimmedTerm.split(/\s+/).filter(Boolean);
    // A multi-word search (e.g. "linen tops") rarely appears verbatim as a
    // whole phrase in any one collection name — see bfs-search-form.liquid's
    // own highlightSearchTerm for the full explanation. Try the whole
    // phrase first, then fall back to bolding whichever individual words
    // actually appear.
    var alternatives = [];
    if (words.length > 1) {
      alternatives.push(words.map(highlightWordPattern).join('\\s+'));
    }
    words.forEach(function(word) {
      alternatives.push(highlightWordPattern(word));
    });
    var pattern = '(?:' + alternatives.join('|') + ')';
    var links = container.querySelectorAll('.bfs-search-results__collections-link');
    for (var i = 0; i < links.length; i++) {
      var link = links[i];
      var regex = new RegExp('(' + pattern + ')', 'gi');
      // Wrapped in a single <span> — this link is display:flex, and a bare
      // text node next to an element as flex children can have its edge
      // whitespace trimmed by the browser. See bfs-search-form.liquid's own
      // highlightSearchTerm for the full explanation.
      link.innerHTML = '<span>' + link.textContent.replace(regex, '<strong>$1</strong>') + '</span>';
    }
  }

  /**
   * Recent Searches — client-side history stored in localStorage (Shopify
   * has no server-side equivalent), shown above Top Suggestions on the
   * pre-typing default panel and hidden entirely when there's no history.
   * Mirrors the copy of this same feature in snippets/bfs-search-form.liquid
   * (same localStorage key, so history saved on one search surface shows up
   * on the other too) — kept as its own copy since this overlay is a
   * separate, older search implementation that doesn't share a JS scope
   * with that file (see highlightSearchTerm's own comment above for the
   * same reasoning).
   */
  var RECENT_SEARCHES_KEY = 'bfsRecentSearches';
  var MAX_RECENT_SEARCHES = 6;

  function getRecentSearches() {
    try {
      var raw = window.localStorage.getItem(RECENT_SEARCHES_KEY);
      var parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function saveRecentSearch(term) {
    try {
      var trimmed = (term || '').trim();
      if (trimmed.length < 2) return;
      var recent = getRecentSearches();
      var lower = trimmed.toLowerCase();
      // Collapse consecutive typing-pause searches on the same word into
      // one entry (e.g. "dre", then a moment later "dress") rather than
      // logging every intermediate stop as its own history item — if the
      // most recent entry and this term are a prefix of one another, update
      // it in place instead of adding a new one.
      if (recent.length && typeof recent[0] === 'string') {
        var frontLower = recent[0].toLowerCase();
        if (frontLower.indexOf(lower) === 0 || lower.indexOf(frontLower) === 0) {
          recent[0] = trimmed;
          window.localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(recent));
          return;
        }
      }
      recent = recent.filter(function(t) { return String(t).toLowerCase() !== lower; });
      recent.unshift(trimmed);
      if (recent.length > MAX_RECENT_SEARCHES) recent = recent.slice(0, MAX_RECENT_SEARCHES);
      window.localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(recent));
    } catch (e) {
      // localStorage can throw (private browsing, storage disabled, quota)
      // — fail silently, same as everything else additive in this file.
    }
  }

  function escapeRecentSearchTerm(str) {
    return String(str).replace(/[&<>"']/g, function(c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function renderRecentSearches(container) {
    if (!container) return;
    var wrapper = container.querySelector('.bfs-search-default__recent');
    if (!wrapper) return;
    var list = wrapper.querySelector('.bfs-search-default__recent-list');
    if (!list) return;
    var recent = getRecentSearches();
    if (!recent.length) {
      wrapper.hidden = true;
      list.innerHTML = '';
      return;
    }
    list.innerHTML = recent.map(function(term) {
      var safe = escapeRecentSearchTerm(term);
      return '<li class="bfs-search-default__recent-item">' +
        '<button type="button" class="bfs-search-default__recent-tag" data-search-term="' + safe + '">' +
        safe + '</button></li>';
    }).join('');
    wrapper.hidden = false;
    capChipRows(list, '.bfs-search-default__recent-item', MAX_RECENT_SEARCH_ROWS);
  }

  // Caps a pill list's visible items to at most maxRows — see
  // bfs-search-form.liquid's own copy of this for the full explanation.
  // Shared by Recent Searches (above) and Top Suggestions (below).
  var MAX_RECENT_SEARCH_ROWS = 3;
  var MAX_TOP_SUGGESTIONS_ROWS = 3;
  function capChipRows(list, itemSelector, maxRows) {
    var items = list.querySelectorAll(itemSelector);
    var rowTops = [];
    for (var i = 0; i < items.length; i++) {
      var top = items[i].offsetTop;
      if (rowTops.indexOf(top) === -1) rowTops.push(top);
      if (rowTops.length > maxRows) {
        for (var j = i; j < items.length; j++) {
          items[j].style.display = 'none';
        }
        return;
      }
    }
  }

  // Top Suggestions is server-rendered static markup rather than JS-
  // populated like Recent Searches — see bfs-search-form.liquid's own copy
  // of this for the full explanation.
  function capTopSuggestionsRows(container) {
    if (!container) return;
    var lists = container.querySelectorAll('.bfs-search-default__collections-list');
    for (var i = 0; i < lists.length; i++) {
      capChipRows(lists[i], '.bfs-search-default__collections-item', MAX_TOP_SUGGESTIONS_ROWS);
    }
  }

  function clearRecentSearches(container) {
    try {
      window.localStorage.removeItem(RECENT_SEARCHES_KEY);
    } catch (e) {}
    renderRecentSearches(container);
  }

  /**
   * Replace the results container content with a smooth crossfade.
   */
  function replaceContent(html) {
    if (!searchResults) return;

    searchResults.style.opacity = '0';
    searchResults.innerHTML = html;
    renderRecentSearches(searchResults);
    capTopSuggestionsRows(searchResults);

    requestAnimationFrame(function() {
      searchResults.style.opacity = '1';
    });
  }

  function showDefaultState() {
    if (!searchResults) return;
    hasResults = false;

    if (defaultStateHTML) {
      replaceContent(defaultStateHTML);
      searchResults.removeAttribute('data-current-query');
      initSwipers();
      initSwatchesDebounced();
      return;
    }

    var sectionId = searchOverlay ? searchOverlay.getAttribute('data-section-id') : null;
    if (!sectionId) return;

    fetch('/?section_id=' + sectionId, {
      method: 'GET',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
    .then(function(response) { return response.text(); })
    .then(function(html) {
      var parser = new DOMParser();
      var doc = parser.parseFromString(html, 'text/html');
      var defaultContent = doc.querySelector('#bfs-search-results');

      if (defaultContent && searchResults) {
        defaultStateHTML = defaultContent.innerHTML;
        replaceContent(defaultStateHTML);
        searchResults.removeAttribute('data-current-query');
        initSwipers();
        initSwatchesDebounced();
      }
    })
    .catch(function() {});
  }

  /**
   * Subtle loading indicator — adds a class instead of replacing content.
   */
  function showLoadingIndicator() {
    if (searchResults) {
      searchResults.classList.add('is-loading');
    }
  }

  function hideLoadingIndicator() {
    if (searchResults) {
      searchResults.classList.remove('is-loading');
    }
  }

  function showErrorState() {
    if (!searchResults) return;
    hasResults = false;
    replaceContent(
      '<div class="bfs-search-results__error">' +
      '<p>Sorry, something went wrong. Please try again.</p>' +
      '</div>'
    );
  }

  /**
   * Initialize Swipers once after a short RAF-based delay.
   */
  function initSwipers() {
    requestAnimationFrame(function() {
      setTimeout(function() {
        if (window.initSearchResultsSwiper) window.initSearchResultsSwiper();
        if (window.initDefaultSwiper) window.initDefaultSwiper();

        var swiperInstance = document.querySelector('.bfs-search-results-products');
        if (swiperInstance && swiperInstance.swiper) {
          swiperInstance.swiper.update();
        }

        var defaultSwiperElements = document.querySelectorAll('[class*="bfs-search-default-products-"]');
        defaultSwiperElements.forEach(function(el) {
          if (el.swiper) el.swiper.update();
        });
      }, 100);
    });
  }

  var swatchTimer = null;
  function initSwatchesDebounced() {
    if (swatchTimer) clearTimeout(swatchTimer);
    swatchTimer = setTimeout(function() {
      if (window.initSwatches) window.initSwatches();
    }, 300);
  }

  function open() {
    if (!searchOverlay) return;
    if (isOpen) {
      if (searchInput) searchInput.focus();
      return;
    }

    scrollPosition = window.pageYOffset || document.documentElement.scrollTop;

    isOpen = true;
    searchOverlay.setAttribute('aria-hidden', 'false');
    searchOverlay.classList.add('is-open');
    document.body.classList.add('bfs-search-overlay-open');
    document.body.style.top = '-' + scrollPosition + 'px';

    var searchDrawerWrapper = document.querySelector('.bfs-search-drawer-wrapper');
    if (searchDrawerWrapper) {
      searchDrawerWrapper.classList.remove('is-open');
      searchDrawerWrapper.style.display = 'none';
      searchDrawerWrapper.style.visibility = 'hidden';
      searchDrawerWrapper.style.opacity = '0';
      searchDrawerWrapper.style.pointerEvents = 'none';
    }

    // No longer auto-focusing the input the instant this opens (was
    // searchInput.focus() + two deferred retries) — that meant the field
    // was always already focused by the time anyone could actually SEE the
    // overlay, so the rotating placeholder animation (which pauses while
    // focused, by design) never got a chance to actually show, and the
    // clear (X) icon's own toggle — now based on whether the field has a
    // value, not focus — would otherwise show X immediately too, before
    // there's anything typed to clear. Leaving focus to an explicit click
    // into the field instead, same as it already works for the mobile
    // drawer's own search bar.

    if (!currentQuery || currentQuery.length === 0) {
      showDefaultState();
    }

    updateAriaAttributes();
  }

  function close() {
    if (!searchOverlay || !isOpen) return;

    isOpen = false;
    cancelInflightRequest();

    if (searchTimeout) {
      clearTimeout(searchTimeout);
      searchTimeout = null;
    }

    searchOverlay.setAttribute('aria-hidden', 'true');
    searchOverlay.classList.remove('is-open');
    document.body.classList.remove('bfs-search-overlay-open');
    document.body.style.top = '';
    window.scrollTo(0, scrollPosition);

    var oldSearchModal = document.getElementById('Search');
    if (oldSearchModal) {
      oldSearchModal.style.display = 'none';
      oldSearchModal.style.visibility = 'hidden';
      oldSearchModal.setAttribute('aria-hidden', 'true');
    }

    var searchDrawerWrapper = document.querySelector('.bfs-search-drawer-wrapper');
    if (searchDrawerWrapper) {
      searchDrawerWrapper.classList.remove('is-open');
      searchDrawerWrapper.style.display = 'none';
      searchDrawerWrapper.style.visibility = 'hidden';
      searchDrawerWrapper.style.opacity = '0';
      searchDrawerWrapper.style.pointerEvents = 'none';
    }

    if (searchInput) {
      searchInput.value = '';
      currentQuery = '';
      searchInput.removeAttribute('data-current-query');
    }

    if (searchResults) {
      searchResults.removeAttribute('data-current-query');
    }

    hasResults = false;
    updateAriaAttributes();
  }

  function updateAriaAttributes() {
    if (!searchOverlay) return;
    var hidden = !isOpen;
    searchOverlay.setAttribute('aria-hidden', hidden.toString());
    if (closeButton) {
      closeButton.setAttribute('aria-expanded', (!hidden).toString());
    }
  }

  function handleTrendingTagClick(e) {
    var tag = e.target;
    var searchTerm = tag.getAttribute('data-search-term');
    if (searchTerm && searchInput) {
      searchInput.value = searchTerm;
      currentQuery = searchTerm;
      performSearch(searchTerm);
    }
  }

  document.addEventListener('click', function(e) {
    if (e.target.classList.contains('bfs-search-default__trending-tag') ||
        e.target.classList.contains('bfs-search-default__recent-tag')) {
      e.preventDefault();
      handleTrendingTagClick(e);
    } else if (e.target.classList.contains('bfs-search-default__recent-clear')) {
      e.preventDefault();
      clearRecentSearches(searchResults);
    }
  });

  function handleCollectionLinkClick(e) {
    var link = e.target.closest('a');
    if (!link || !link.href) return;

    var isCollectionLink = link.classList.contains('bfs-search-default__collections-link') ||
                           link.classList.contains('bfs-search-results__collections-link');
    if (!isCollectionLink) return;
    if (!searchOverlay || !searchOverlay.contains(link)) return;

    var searchQuery = currentQuery;
    if (!searchQuery && searchResults) {
      searchQuery = searchResults.getAttribute('data-current-query') || '';
    }
    if (!searchQuery && searchInput) {
      searchQuery = searchInput.getAttribute('data-current-query') || searchInput.value.trim() || '';
    }

    if (searchQuery && searchQuery.trim().length > 0) {
      e.preventDefault();
      e.stopPropagation();

      var url;
      try {
        url = new URL(link.href);
      } catch (err) {
        url = new URL(link.href, window.location.origin);
      }

      url.searchParams.set('q', searchQuery.trim());
      window.location.href = url.toString();
      return false;
    }
  }

  document.addEventListener('click', function(e) {
    handleCollectionLinkClick(e);
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.bfsSearch = {
    open: open,
    close: close,
    isOpen: function() { return isOpen; }
  };

})();

// Rotating search-suggestion placeholder for the desktop search overlay —
// ports snippets/bfs-search-form.liquid's own copy of this (mobile drawer)
// verbatim, just re-targeted at #bfs-search-overlay-input instead of
// .bfs-mobile-navigation__search's input; see that file's own comment for
// the full explanation of the type/erase/hold timing. "Search " stays
// fixed; only the suggestion word after it types in, holds, erases, and
// moves to the next — pausing whenever the input is focused or has a typed
// value, resuming once it's blank and unfocused again.
(function() {
  var BASE_LABEL = 'Search';
  var SUGGESTIONS = ['Polos', 'Homewares', 'Linen', 'Bedding'];
  var TYPE_MS = 90;
  var ERASE_MS = 45;
  var HOLD_MS = 1500;
  var GAP_MS = 400;
  var RETRY_MS = 300;
  var prefixLen = BASE_LABEL.length + 1;

  function bfsInitOverlaySearchPlaceholderRotator() {
    var input = document.getElementById('bfs-search-overlay-input');
    if (!input) return;

    var wordIndex = 0;

    function isBusy() {
      var el = document.getElementById('bfs-search-overlay-input');
      return !el || el === document.activeElement || !!el.value;
    }

    function getInput() {
      return document.getElementById('bfs-search-overlay-input');
    }

    function typeWord() {
      if (isBusy()) { setTimeout(typeWord, RETRY_MS); return; }
      var full = BASE_LABEL + ' ' + SUGGESTIONS[wordIndex % SUGGESTIONS.length];
      var i = prefixLen;
      getInput().setAttribute('placeholder', full.slice(0, i));

      function step() {
        if (isBusy()) { setTimeout(typeWord, RETRY_MS); return; }
        i++;
        getInput().setAttribute('placeholder', full.slice(0, i));
        if (i < full.length) {
          setTimeout(step, TYPE_MS);
        } else {
          setTimeout(eraseWord, HOLD_MS);
        }
      }
      setTimeout(step, TYPE_MS);
    }

    function eraseWord() {
      if (isBusy()) { setTimeout(typeWord, RETRY_MS); return; }
      var full = BASE_LABEL + ' ' + SUGGESTIONS[wordIndex % SUGGESTIONS.length];
      var i = full.length;

      function step() {
        if (isBusy()) { setTimeout(typeWord, RETRY_MS); return; }
        i--;
        getInput().setAttribute('placeholder', full.slice(0, i));
        if (i > prefixLen) {
          setTimeout(step, ERASE_MS);
        } else {
          wordIndex++;
          setTimeout(typeWord, GAP_MS);
        }
      }
      setTimeout(step, ERASE_MS);
    }

    // Snaps the placeholder back to the bare base label the instant the
    // field is focused, regardless of where mid-word the rotator happened
    // to be — same reasoning as the mobile drawer's own copy.
    input.addEventListener('focusin', function(e) {
      var el = getInput();
      if (el && e.target === el) {
        el.setAttribute('placeholder', BASE_LABEL);
      }
    });

    typeWord();
  }

  function bfsInitOverlaySearchPlaceholderRotatorSafe() {
    try {
      bfsInitOverlaySearchPlaceholderRotator();
    } catch (err) {
      if (window.console && console.error) {
        console.error('bfs overlay search placeholder rotator failed to initialize:', err);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bfsInitOverlaySearchPlaceholderRotatorSafe);
  } else {
    bfsInitOverlaySearchPlaceholderRotatorSafe();
  }
})();
